import { useEffect, useRef, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { CheckmarkThin, CrossSmall } from "@acto/web-common/components";
import { ToastCapsule, ToastMessage } from "./ToastCapsule";

/** Default time a toast stays visible before it auto-hides, in ms. */
export const TOAST_DEFAULT_DURATION_MS = 3000;

const Icon = styled("span")<{ tone?: "success" | "destructive" }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.fieldSalesTwo.colors.status.success.main,
  variants: [{ props: { tone: "destructive" }, style: { color: theme.fieldSalesTwo.colors.status.error.main } }],
}));

interface ToastProps {
  open: boolean;
  message: string;
  onClose: () => void;
  /** Auto-dismiss delay in ms. */
  durationMs?: number;
  /** Leading icon. Defaults per tone (checkmark, or a cross for destructive). Pass one for a context-specific glyph. */
  icon?: ReactNode;
  /** Icon tint. "destructive" (red) for delete confirmations; defaults to success green. */
  tone?: "success" | "destructive";
  /** Applies `tone` to the message text as well as the icon. */
  tintMessage?: boolean;
}

/** Transient toast confirmation, auto-dismisses. `tone` tints the icon (success green / destructive red). */
export function Toast({
  open,
  message,
  onClose,
  durationMs = TOAST_DEFAULT_DURATION_MS,
  tone = "success",
  tintMessage = false,
  icon = tone === "destructive" ? <CrossSmall aria-hidden="true" /> : <CheckmarkThin aria-hidden="true" />,
}: ToastProps) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => onCloseRef.current(), durationMs);
    return () => clearTimeout(timer);
  }, [open, durationMs]);

  if (!open) return null;

  return (
    <ToastCapsule role="status" aria-live="polite">
      <Icon tone={tone}>{icon}</Icon>
      <ToastMessage tone={tintMessage ? tone : undefined}>{message}</ToastMessage>
    </ToastCapsule>
  );
}
